import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://dgfyjipsmuthvobrvfqh.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRnZnlqaXBzbXV0aHZvYnJ2ZnFoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgyMzA0MDQsImV4cCI6MjA2MzgwNjQwNH0.5PhtDOwkcrnBeCpahNA7acbaKJzBj6yxpx2U3wAOItM';

// Create the base Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true
  },
  global: {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal'
    }
  },
  // Enable auto-refresh of auth tokens
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  // Set longer timeout for better reliability
  db: {
    schema: 'public'
  }
});

// Cache for frequently accessed data
class QueryCache {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private ttl: number = 30000; // 30 seconds default TTL

  constructor(ttlMs?: number) {
    if (ttlMs) this.ttl = ttlMs;
  }

  generateKey(table: string, query: string, params?: any): string {
    return `${table}:${query}:${params ? JSON.stringify(params) : ''}`;
  }

  get(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    // Check if cache is still valid
    if (Date.now() - cached.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  set(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  invalidate(table: string): void {
    // Remove all cached items for a specific table
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${table}:`)) {
        this.cache.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }
}

// Create a global query cache instance
export const queryCache = new QueryCache();

// Enhanced Supabase client with caching
export const enhancedSupabase = {
  // Cached select query
  async selectWithCache(table: string, query: string, params?: any, forceFresh: boolean = false) {
    const cacheKey = queryCache.generateKey(table, query, params);
    
    if (!forceFresh) {
      const cachedData = queryCache.get(cacheKey);
      if (cachedData) {
        return { data: cachedData, error: null, source: 'cache' };
      }
    }
    
    // If not in cache or force fresh, fetch from Supabase
    const { data, error } = await supabase.from(table).select(query, params);
    
    if (!error && data) {
      queryCache.set(cacheKey, data);
    }
    
    return { data, error, source: 'api' };
  },
  
  // Invalidate cache after mutations
  async insert(table: string, values: any) {
    const result = await supabase.from(table).insert(values);
    queryCache.invalidate(table);
    return result;
  },
  
  async update(table: string, values: any, match: Record<string, any>) {
    const query = supabase.from(table).update(values);
    
    // Apply all matching conditions
    Object.entries(match).forEach(([key, value]) => {
      query.eq(key, value);
    });
    
    const result = await query;
    queryCache.invalidate(table);
    return result;
  },
  
  async delete(table: string, match: Record<string, any>) {
    const query = supabase.from(table).delete();
    
    // Apply all matching conditions
    Object.entries(match).forEach(([key, value]) => {
      query.eq(key, value);
    });
    
    const result = await query;
    queryCache.invalidate(table);
    return result;
  }
};

// Type definitions for our database tables
export type Tables = {
  customers: {
    id: string;
    phone_number: string;
    name: string | null;
    email: string | null;
    created_at: string;
  };
  visits: {
    id: string;
    customer_id: string;
    date: string;
    created_at: string;
  };
  redeemed_rewards: {
    id: string;
    customer_id: string;
    reward_id: string;
    date: string;
    created_at: string;
  };
}; 